// @flow
import { useEffect, useRef, useState } from 'react';
import throttle from 'lodash/throttle';

const useIntersectionObserver = (ref, rootMargin, threshold = 0) => {
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    if (!ref.current) {
      setIsIntersecting(true);
      return () => {};
    }

    if (!isIntersecting) {
      // Fallback to onScroll if IntersectionObserver not available in browser
      if (typeof IntersectionObserver === 'undefined') {
        const onScroll = throttle(() => {
          const viewportBottom = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
          const elementTop = ref.current.getBoundingClientRect().top;

          if (elementTop <= viewportBottom * (1 - threshold)) {
            setIsIntersecting(true);
          }
        }, 300);

        window.addEventListener('scroll', onScroll);

        return () => { window.removeEventListener('scroll', onScroll); };
      }

      const options = {
        root: null,
        rootMargin,
        threshold
      };

      const callback = (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsIntersecting(true);
            observer.disconnect();
          }
        });
      };

      const observer = new IntersectionObserver(callback, options);

      observer.observe(ref.current);

      return () => {
        observer.disconnect();
      };
    }

    return () => {};
  }, [ref, rootMargin, threshold, isIntersecting, setIsIntersecting]);

  return isIntersecting;
};

// rootMargin: triggers intersection before ref enters viewport, to allow
// time for the asset to load.
const useLazyLoad = ({ rootMargin = '100px', ref: providedRef } = {}) => {
  let ref = useRef<any>();

  // Use provided ref
  if (providedRef) {
    ref = providedRef;
  }

  const isIntersectingMargin = useIntersectionObserver(ref, rootMargin, 0);
  const isIntersectingElement = useIntersectionObserver(ref, '0px', 0);
  const isIntersectingCenter = useIntersectionObserver(ref, '0px', 0.5);

  return {
    ref,
    isIntersectingMargin,
    isIntersectingElement,
    isIntersectingCenter
  };
};

export default useLazyLoad;
